import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { SERVICE_OPTIONS, getServiceTitle } from "./data/serviceOptions";
import schoolsData from "./data/schools.json";

const NURSE_OPTIONS = [
  "Louise Chambers",
  "Sharon Andrews",
  "Yvonne Allen",
  "Caroline Marshall",
  "Monica Garnett",
  "Denise Bennion",
  "Tricia Baxendale",
  "Janet Pearce",
];

const ASSESSING_NURSE_CACHE_KEY = "assessingNurseSelection";
const SCHOOL_SELECTION_CACHE_KEY = "schoolSelection";
const SELECTION_CACHE_MS = 8 * 60 * 60 * 1000;
const SCHOOL_SEARCH_LIMIT = 8;

const normalizeSearchValue = (value) =>
  String(value || "")
    .toLowerCase()
    .trim();

const getSchoolDisplayLabel = (school) => {
  if (!school) return "";

  const urnPrefix = school.urn ? `${school.urn} - ` : "";
  const postcodeSuffix = school.postcode ? ` (${school.postcode})` : "";

  return `${urnPrefix}${school.name || ""}${postcodeSuffix}`.trim();
};

const SelectionPage = () => {
  const navigate = useNavigate();
  const [assessingNurse, setAssessingNurse] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);

  const filteredSchools = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(schoolSearch);
    if (!normalizedSearch) return [];

    return schoolsData
      .filter((school) => {
        const valuesToSearch = [school.name, school.postcode, school.urn];

        return valuesToSearch.some((value) =>
          normalizeSearchValue(value).includes(normalizedSearch),
        );
      })
      .slice(0, SCHOOL_SEARCH_LIMIT);
  }, [schoolSearch]);

  useEffect(() => {
    try {
      const rawCache = localStorage.getItem(ASSESSING_NURSE_CACHE_KEY);
      if (!rawCache) return;

      const parsedCache = JSON.parse(rawCache);
      const hasExpired =
        !parsedCache?.expiresAt || Date.now() > parsedCache.expiresAt;

      if (hasExpired) {
        localStorage.removeItem(ASSESSING_NURSE_CACHE_KEY);
        return;
      }

      setAssessingNurse(parsedCache.value || "");
    } catch (error) {
      localStorage.removeItem(ASSESSING_NURSE_CACHE_KEY);
    }

    try {
      const rawSchoolCache = localStorage.getItem(SCHOOL_SELECTION_CACHE_KEY);
      if (!rawSchoolCache) return;

      const parsedSchoolCache = JSON.parse(rawSchoolCache);
      const hasSchoolCacheExpired =
        !parsedSchoolCache?.expiresAt ||
        Date.now() > parsedSchoolCache.expiresAt;

      if (hasSchoolCacheExpired) {
        localStorage.removeItem(SCHOOL_SELECTION_CACHE_KEY);
        return;
      }

      const cachedSchool = parsedSchoolCache.value;
      setSelectedSchool(cachedSchool || null);
      setSchoolSearch(getSchoolDisplayLabel(cachedSchool));
    } catch (error) {
      localStorage.removeItem(SCHOOL_SELECTION_CACHE_KEY);
    }
  }, []);

  const handleAssessingNurseChange = (value) => {
    setAssessingNurse(value);

    if (!value) {
      localStorage.removeItem(ASSESSING_NURSE_CACHE_KEY);
      return;
    }

    const cachePayload = {
      value,
      expiresAt: Date.now() + SELECTION_CACHE_MS,
    };

    localStorage.setItem(
      ASSESSING_NURSE_CACHE_KEY,
      JSON.stringify(cachePayload),
    );
  };

  const handleSchoolInputChange = (value) => {
    setSchoolSearch(value);

    if (selectedSchool && value !== getSchoolDisplayLabel(selectedSchool)) {
      setSelectedSchool(null);
      localStorage.removeItem(SCHOOL_SELECTION_CACHE_KEY);
    }
  };

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    setSchoolSearch(getSchoolDisplayLabel(school));

    const cachePayload = {
      value: school,
      expiresAt: Date.now() + SELECTION_CACHE_MS,
    };

    localStorage.setItem(
      SCHOOL_SELECTION_CACHE_KEY,
      JSON.stringify(cachePayload),
    );
  };

  const showSchoolResults = schoolSearch.trim().length > 0 && !selectedSchool;

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50">
      <button
        className="absolute top-0 right-0"
        onClick={() => navigate("/login")}
        aria-label="Login"
      >
        <FontAwesomeIcon
          icon={faRightToBracket}
          className="text-sas-primary text-2xl mr-2 mt-2 hover:text-blue-700"
        />
      </button>

      <div className="mx-auto flex min-h-screen max-w-5xl flex-col justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-2xl border border-sky-200 bg-white p-4 shadow-sm">
          <label
            htmlFor="school-search"
            className="block text-xs font-semibold uppercase tracking-[0.22em] text-sas-primary"
          >
            Search for your School / Academy below
          </label>
          <input
            id="school-search"
            type="text"
            className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30"
            placeholder="Type school name, URN or postcode"
            value={schoolSearch}
            onChange={(e) => handleSchoolInputChange(e.target.value)}
          />
          {showSchoolResults && filteredSchools.length > 0 ? (
            <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {filteredSchools.map((school) => (
                <button
                  key={school.id}
                  type="button"
                  className="block w-full border-b border-slate-100 px-4 py-3 text-left text-sm font-semibold text-sas-primary-dark transition hover:bg-sky-50 last:border-b-0"
                  onClick={() => handleSchoolSelect(school)}
                >
                  {getSchoolDisplayLabel(school)}
                </button>
              ))}
            </div>
          ) : null}
          <p className="mt-2 text-xs text-slate-500 font-montserrat">
            This selection is remembered for 8 hours on this device.
          </p>
        </div>

        <div className="mb-6 rounded-2xl border border-sky-200 bg-white p-4 shadow-sm">
          <label
            htmlFor="assessing-nurse"
            className="block text-xs font-semibold uppercase tracking-[0.22em] text-sas-primary"
          >
            Assessing Nurse
          </label>
          <select
            id="assessing-nurse"
            className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30"
            value={assessingNurse}
            onChange={(e) => handleAssessingNurseChange(e.target.value)}
          >
            <option value="" disabled>
              Select nurse
            </option>
            {NURSE_OPTIONS.map((nurseName) => (
              <option key={nurseName} value={nurseName}>
                {nurseName}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-slate-500 font-montserrat">
            This selection is remembered for 8 hours on this device.
          </p>
        </div>
        <div className="mx-auto grid w-full gap-4">
          {SERVICE_OPTIONS.map((service) => (
            <button
              key={service.value}
              type="button"
              onClick={() =>
                navigate(`/screen/${service.path}`, {
                  state: {
                    service: service.value,
                    assessingNurse,
                    selectedSchool,
                  },
                })
              }
              disabled={!assessingNurse || !selectedSchool}
              className="group w-full rounded-[1.75rem] border border-slate-200 bg-white p-6 text-left shadow-md transition hover:-translate-y-1 hover:border-sas-primary hover:shadow-xl"
            >
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sas-primary">
                Select
              </div>
              <h2 className="mt-3 text-2xl font-bold text-slate-900 font-fatfrank">
                {getServiceTitle(service.value)}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {service.description}
              </p>
              <div className="mt-6 inline-flex items-center rounded-full bg-sas-primary px-4 py-2 text-sm font-semibold text-white transition group-hover:bg-sas-primary-dark group-disabled:bg-slate-300 group-disabled:text-slate-600">
                Start this session
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectionPage;
